import { useEffect, useState } from 'react';
import {
  useTranslate,
  useNotification,
  useInvalidate,
  useCustomMutation,
  useNavigation,
} from '@refinedev/core';
import { Alert, Form, Input, Modal, Space } from 'antd';
import { extractBackendMessage } from '@/utils/apiError';
import type {
  EntitlementDetailOut,
  RevenueCatBillingPeriodPrecedenceIn,
  RevenueCatBillingPeriodPrecedenceRefusal,
} from '@/api/generated/schemas';
import { EntitlementLinks } from '@/components/monetization/EntitlementLinks';
import { extractPrecedenceRefusal } from '@/components/monetization/precedenceRefusal';

const REASON_MAX_LENGTH = 500;

interface GivePrecedenceModalProps {
  billingPeriodId: number;
  /** Entitlements overlapping the period's window, named in the warning. */
  conflictingEntitlementIds?: number[];
  open: boolean;
  onClose: () => void;
}

export const GivePrecedenceModal = ({
  billingPeriodId,
  conflictingEntitlementIds = [],
  open,
  onClose,
}: GivePrecedenceModalProps) => {
  const t = useTranslate();
  const { open: notify } = useNotification();
  const invalidate = useInvalidate();
  const { show } = useNavigation();
  const { mutateAsync } = useCustomMutation();
  const [form] = Form.useForm<RevenueCatBillingPeriodPrecedenceIn>();
  const [submitting, setSubmitting] = useState(false);
  const [refusal, setRefusal] =
    useState<RevenueCatBillingPeriodPrecedenceRefusal>();
  const reason = Form.useWatch('reason', form);

  const tk = (suffix: string) =>
    t(`monetization.revenuecat_billing_periods.give_precedence.${suffix}`);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setSubmitting(false);
      setRefusal(undefined);
    }
  }, [open, form]);

  const invalidateBillingPeriod = () =>
    invalidate({
      resource: 'monetization.revenuecat_billing_periods',
      invalidates: ['detail', 'list'],
      id: billingPeriodId,
    });

  const handleSubmit = async () => {
    let values: RevenueCatBillingPeriodPrecedenceIn;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setSubmitting(true);
    setRefusal(undefined);
    try {
      const result = await mutateAsync({
        url: `monetization/revenuecat_billing_periods/${billingPeriodId}/give_precedence`,
        method: 'post',
        values: { reason: values.reason.trim() },
        successNotification: false,
        errorNotification: false,
      });
      const created = result?.data as EntitlementDetailOut | undefined;

      notify?.({
        type: 'success',
        message: t('notifications.success'),
        description: tk('success'),
      });
      invalidateBillingPeriod();
      invalidate({
        resource: 'monetization.entitlements',
        invalidates: ['list', 'detail'],
      });
      onClose();
      if (created?.id != null) {
        show('monetization.entitlements', created.id);
      }
    } catch (error) {
      const refused = extractPrecedenceRefusal(error);
      if (refused) {
        setRefusal(refused);
        // The record on screen is stale: refetching it withdraws the trigger.
        if (refused.code === 'already_projected') invalidateBillingPeriod();
        return;
      }
      notify?.({
        type: 'error',
        message: t('notifications.error'),
        description: extractBackendMessage(error) ?? tk('error_fallback'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const submitDisabled = !reason || reason.trim() === '' || submitting;

  return (
    <Modal
      open={open}
      title={tk('title')}
      okText={tk('submit')}
      okButtonProps={{
        danger: true,
        disabled: submitDisabled,
        loading: submitting,
      }}
      cancelText={t('common.cancel')}
      cancelButtonProps={{ disabled: submitting }}
      closable={!submitting}
      maskClosable={!submitting}
      onCancel={onClose}
      onOk={handleSubmit}
      destroyOnClose
      width={520}
    >
      <Alert
        type="warning"
        showIcon
        message={tk('warning')}
        description={
          conflictingEntitlementIds.length > 0 && (
            <Space size="small" wrap>
              {tk('conflicting_entitlements')}
              <EntitlementLinks ids={conflictingEntitlementIds} newTab />
            </Space>
          )
        }
        style={{ marginBottom: 16 }}
      />
      {refusal && (
        <Alert
          type="error"
          showIcon
          message={tk(`refusal.${refusal.code}`)}
          description={
            refusal.entitlement_ids.length > 0 && (
              <Space size="small" wrap>
                {tk('refusal.entitlements')}
                <EntitlementLinks ids={refusal.entitlement_ids} newTab />
              </Space>
            )
          }
          style={{ marginBottom: 16 }}
        />
      )}
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        disabled={submitting}
      >
        <Form.Item
          name="reason"
          label={tk('fields.reason')}
          rules={[
            {
              required: true,
              whitespace: true,
              message: tk('errors.reason_required'),
            },
          ]}
          extra={tk('hints.reason')}
        >
          <Input.TextArea
            rows={3}
            maxLength={REASON_MAX_LENGTH}
            showCount
            placeholder={tk('placeholders.reason')}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
